import { useCallback, useMemo, useRef, useState } from 'react';
import PricingPlanFeature from 'src/entities/pricing/pricingPlanFeature';
import { createProjectForm, createProjectWithoutWorkspaceForm } from '../../../../../forms/createProject';
import useFormWorkspaces from './useFormWorkspaces';
import useFormFieldsEventHandlers from './useFormFieldsEventHandlers';
import useFormErrors from './useFormErrors';
import useFormInviteMembersOptions from './useFormInviteMembersOptions';
import useFormLabelGroupsOptions from './useFormLabelGroupsOptions';

type CreateProjectFormData = {
	name: string;
	startDate: Date | null;
	dueDate: Date | null;
	assetsStartRetouched: boolean;
	workspaceID: number | null;
	clientName: string;
	inviteAllMembers: boolean;
	inviteContactGroups: number[];
	selectedLabelGroups: number[];
};

type CreateProjectFormProps = {
	initialWorkspaceID?: number,
	onSubmit: ( data: CreateProjectFormData ) => void
};

const useCreateProjectForm = ( {
	initialWorkspaceID,
	onSubmit
}: CreateProjectFormProps ) => {
	const startDateRef = useRef<HTMLInputElement>( null );
	const dueDateRef = useRef<HTMLInputElement>( null );
	const {
		workspaces,
		skipWorkspaceSelection,
		initialSelectedWorkspaceID,
		workspaceOptions
	} = useFormWorkspaces( { initialWorkspaceID } );

	const createForm = useMemo(
		() => ( skipWorkspaceSelection ? createProjectWithoutWorkspaceForm : createProjectForm ),
		[ skipWorkspaceSelection ]
	);

	const [ formState, setFormState ] = useState( createForm( {
		name: '',
		startDate: new Date(),
		assetsStartRetouched: false,
		workspaceID: initialSelectedWorkspaceID,
		clientName: '',
		inviteAllMembers: false,
		inviteContactGroups: [],
		selectedLabelGroups: []
	}, workspaces ) );

	const { data } = formState;

	const selectedWorkspace = useMemo( () => {
		if ( !data.workspaceID ) { return null; }
		return workspaces.find( workspace => workspace.id === data.workspaceID ) || null;
	}, [ data.workspaceID, workspaces ] );

	const isContactGroupsFeatureAvailable = useMemo(
		() => selectedWorkspace?.supportsFeature( PricingPlanFeature.CONTACT_GROUPS ) || false,
		[ selectedWorkspace ]
	);

	const isLabelGroupsFeatureAvailable = useMemo(
		() => selectedWorkspace?.supportsFeature( PricingPlanFeature.LABEL_GROUPS ) || false,
		[ selectedWorkspace ]
	);

	const [ editedFields, setEditedFields ] = useState<Record<string, boolean>>( {} );
	const isValid = useMemo( () => formState.validate(), [ formState.data ] );

	const {
		onNameInputBlur,
		onClientNameInputBlur,
		onNameInputChange,
		onClientNameInputChange,
		onStartAtChange,
		onDueDateChange,
		onWorkspaceSelectChange,
		onAssetsStartRetouchedSelectChange,
		onInviteAllMembersChange
	} = useFormFieldsEventHandlers( { formState, setFormState, setEditedFields } );

	const {
		options: inviteMembersOptions,
		selected: inviteMembersSelected,
		onOptionChange: onInviteMembersChange
	} = useFormInviteMembersOptions( {
		selectedWorkspaceID: data.workspaceID,
		isContactGroupsFeatureAvailable,
		formState,
		setFormState
	} );

	const {
		options: labelGroupsOptions,
		selected: labelGroupsSelected,
		onOptionChange: onLabelGroupsChange
	} = useFormLabelGroupsOptions( {
		selectedWorkspaceID: data.workspaceID,
		isLabelGroupsFeatureAvailable,
		formState,
		setFormState
	} );

	const errors = useFormErrors( { formState, editedFields } );

	const onFormSubmitted = useCallback( ( event: React.FormEvent<HTMLFormElement> ) => {
		event.preventDefault();
		if ( !isValid ) { return; }
		onSubmit( data );
	}, [ data, isValid, onSubmit ] );

	return {
		formState,
		errors,
		isValid,
		onNameInputBlur,
		onClientNameInputBlur,
		onNameInputChange,
		onClientNameInputChange,
		onStartAtChange,
		onDueDateChange,
		workspaceOptions,
		showWorkspaces: !skipWorkspaceSelection,
		onFormSubmitted,
		onWorkspaceSelectChange,
		onAssetsStartRetouchedSelectChange,
		onInviteAllMembersChange,
		inviteMembersOptions,
		inviteMembersSelected,
		onInviteMembersChange,
		startDateRef,
		dueDateRef,
		labelGroupsOptions,
		labelGroupsSelected,
		onLabelGroupsChange,
		selectedWorkspace
	}
}

export default useCreateProjectForm;
